/**
 * Invoice PDF Service
 *
 * Generates, stores, retrieves, views, and downloads invoice PDFs.
 * Uses @react-pdf/renderer for client-side PDF generation from
 * billing Invoice data.
 *
 * Storage: PDFs are persisted in Azure Table Storage via the backend
 * API endpoint POST /api/billing/invoices/{id}/pdf.
 */

import { Buffer } from 'buffer';
if (typeof globalThis.Buffer === 'undefined') {
  globalThis.Buffer = Buffer;
}

import { pdf } from '@react-pdf/renderer';
import type { ReactElement } from 'react';
import { createElement } from 'react';
import type { DocumentProps } from '@react-pdf/renderer';
import { InvoicePdfTemplate } from '@/components/invoices/InvoicePdfTemplate';
import { authFetch } from '@/lib/auth-fetch';
import { getApiBaseUrl } from '@/lib/data-service';
import type {
  InvoicePdfData,
  InvoicePdfTransaction,
  InvoicePdfResult,
  InvoicePdfLineItem,
  CompanyDetails,
  StoredInvoicePdf,
} from '@/types/invoice-pdf';
import type { BillingInvoiceLineItem } from '@/lib/data-service';
import type { Company } from '@/types/company';
import type { InvoiceTemplate, InvoicePreviewData, InvoiceLineItem as TemplateLineItem } from '@/lib/invoice-template-types';
import { getInvoiceTemplates, getInvoiceTemplate } from '@/services/invoiceTemplateService';
import { getCompanyDetails, setCurrentCompanyId } from '@/services/companyService';

/** Empty company details — used when no company data is available */
const EMPTY_COMPANY_DETAILS: CompanyDetails = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  country: '',
  phone: '',
  email: '',
  vatNumber: '',
  companyNumber: '',
};

const DEFAULT_PAYMENT_TERMS =
  'Payment is due within 30 days of the invoice date. ' +
  'Please quote the invoice number on all remittances. ' +
  'Late payments may be subject to statutory interest charges.';

/**
 * Build CompanyDetails from the Company entity provided by CompanyContext.
 * Falls back to DEFAULT_COMPANY_DETAILS for any missing fields.
 */
export function buildCompanyDetails(company: Company): CompanyDetails {
  const addr = company.registeredAddress;
  return {
    name: company.name || '',
    addressLine1: addr?.line1 || '',
    addressLine2: addr?.line2 || '',
    city: addr?.city || '',
    postcode: addr?.postcode || '',
    country: addr?.country || '',
    phone: company.phone || '',
    email: company.email || '',
    vatNumber: company.vatNumber || '',
    companyNumber: company.companyRegistrationNumber || '',
    logoUrl: company.logoUrl,
    bankDetails: company.bankDetails,
  };
}

/**
 * Fetch full company details from the detail endpoint and map to the
 * invoice-pdf CompanyDetails shape. Falls back to buildCompanyDetails()
 * if the detail fetch fails or returns empty data.
 *
 * This is the preferred way to obtain company details for PDF generation
 * because the list endpoint (used by CompanyContext) does not return
 * invoicing-specific fields like registered address, VAT number, etc.
 */
export async function fetchFullCompanyDetails(
  company: Company | null,
): Promise<CompanyDetails | undefined> {
  if (!company) return undefined;
  try {
    setCurrentCompanyId(company.id);
    const fullDetails = await getCompanyDetails();
    if (fullDetails.name) {
      return {
        name: fullDetails.name,
        addressLine1: fullDetails.registeredAddress?.line1 || '',
        addressLine2: fullDetails.registeredAddress?.line2 || '',
        city: fullDetails.registeredAddress?.city || '',
        postcode: fullDetails.registeredAddress?.postcode || '',
        country: fullDetails.registeredAddress?.country || '',
        phone: fullDetails.phone || '',
        email: fullDetails.email || '',
        vatNumber: fullDetails.vatNumber || '',
        companyNumber: fullDetails.companyRegistrationNumber || '',
        logoUrl: fullDetails.logoUrl,
        bankDetails: fullDetails.bankDetails,
      };
    }
  } catch {
    // Fallback below
  }
  return buildCompanyDetails(company);
}

/**
 * Convert backend BillingInvoiceLineItems to the PDF line-item shape.
 */
function mapBillingLineItems(items: BillingInvoiceLineItem[]): InvoicePdfLineItem[] {
  return items.map((item) => ({
    description: item.description || 'Professional services',
    code: '',
    quantity: item.quantity,
    unitPrice: item.rate,
    value: item.netAmount,
    taxAmount: item.taxAmount,
    grossAmount: item.grossAmount,
  }));
}

/**
 * Fallback: build a single synthetic line item when no real line items are available.
 */
function buildFallbackLineItems(transaction: InvoicePdfTransaction): InvoicePdfLineItem[] {
  return [
    {
      description: transaction.description || 'Professional services',
      code: 'SRV-GEN',
      quantity: 1,
      unitPrice: transaction.amount,
      value: transaction.amount,
    },
  ];
}

/**
 * Generate a PDF for the given billing invoice.
 *
 * @param billingLineItems — actual line items from the backend. When provided,
 *   these are used instead of a synthetic fallback.
 */
export async function generateInvoicePdf(
  transaction: InvoicePdfTransaction,
  companyDetails: CompanyDetails = EMPTY_COMPANY_DETAILS,
  billingLineItems?: BillingInvoiceLineItem[],
  template?: InvoiceTemplate,
  previewData?: InvoicePreviewData,
): Promise<InvoicePdfResult> {
  const lineItems = billingLineItems && billingLineItems.length > 0
    ? mapBillingLineItems(billingLineItems)
    : buildFallbackLineItems(transaction);

  const pdfData: InvoicePdfData = {
    transaction,
    companyDetails,
    lineItems,
    paymentTerms: DEFAULT_PAYMENT_TERMS,
    template,
    previewData,
  };

  const element = createElement(InvoicePdfTemplate, { data: pdfData }) as ReactElement<DocumentProps>;
  const pdfBlob = await pdf(element).toBlob();

  return {
    invoiceKey: transaction.invoiceNumber,
    invoiceNumber: transaction.invoiceNumber,
    pdfBlob,
    generatedAt: new Date(),
  };
}

// ── Helper: convert Blob → Base64 string ──────────────────────────────────
export async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Save a generated PDF to backend table storage.
 *
 * Calls POST /api/billing/invoices/{id}/pdf with the PDF as Base64.
 * The invoiceId must be a numeric ID returned from the backend.
 */
export async function saveInvoicePdf(
  result: InvoicePdfResult,
  invoiceId?: number | string,
): Promise<void> {
  const id = invoiceId ?? result.invoiceKey;
  const pdfBase64 = await blobToBase64(result.pdfBlob);

  const response = await authFetch(`${getApiBaseUrl()}/billing/invoices/${id}/pdf`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pdfBase64 }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { error?: string }).error ??
        `Failed to save PDF: ${response.status} ${response.statusText}`,
    );
  }
}

/**
 * Retrieve a stored PDF by invoice ID from the backend.
 *
 * Calls GET /api/billing/invoices/{id}/pdf which returns JSON with base64-encoded
 * PDF data. The base64 is decoded client-side into a Blob.
 */
export async function getStoredInvoicePdf(
  invoiceKey: string,
): Promise<StoredInvoicePdf | null> {
  try {
    const response = await authFetch(`${getApiBaseUrl()}/billing/invoices/${invoiceKey}/document`, {
      credentials: 'include',
    });

    if (!response.ok) return null;

    const data: {
      pdfBase64?: string;
      contentType?: string;
      invoiceNumber?: string;
      sizeBytes?: number;
    } = await response.json();

    if (!data.pdfBase64) return null;

    const byteCharacters = atob(data.pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const pdfBlob = new Blob([new Uint8Array(byteNumbers)], {
      type: data.contentType ?? 'application/pdf',
    });

    return {
      invoiceKey,
      invoiceNumber: data.invoiceNumber ?? invoiceKey,
      pdfBlob,
      contentType: data.contentType ?? 'application/pdf',
      generatedAt: new Date(),
      sizeBytes: data.sizeBytes ?? pdfBlob.size,
    };
  } catch {
    return null;
  }
}

/**
 * Check whether a PDF has already been generated for this invoice.
 *
 * Calls GET /api/billing/invoices/{id}/pdf/exists
 */
export async function hasStoredPdf(invoiceKey: string): Promise<boolean> {
  try {
    const response = await authFetch(
      `${getApiBaseUrl()}/billing/invoices/${invoiceKey}/pdf/exists`,
      { credentials: 'include' },
    );
    if (!response.ok) return false;
    const data: { exists?: boolean } = await response.json();
    return data.exists === true;
  } catch {
    return false;
  }
}

/**
 * Check which of the given invoices already have a stored PDF.
 *
 * Calls POST /api/billing/invoices/pdf/exists-batch with all IDs in one
 * request, replacing N individual /exists calls with a single round-trip.
 */
export async function hasStoredPdfBatch(
  invoiceIds: (number | string)[],
): Promise<Set<string>> {
  if (invoiceIds.length === 0) return new Set();
  try {
    const response = await authFetch(
      `${getApiBaseUrl()}/billing/invoices/pdf/exists-batch`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceIds: invoiceIds.map(Number) }),
      },
    );
    if (!response.ok) return new Set();
    const data: { invoiceIds?: number[] } = await response.json();
    return new Set((data.invoiceIds ?? []).map(String));
  } catch {
    return new Set();
  }
}

/**
 * Fetch the default invoice template directly from the API.
 * This is used instead of relying on React context which may not be loaded yet.
 * If a specific templateId is provided, that template is fetched; otherwise the
 * default (or first available) template is returned.
 */
export async function fetchDefaultInvoiceTemplate(
  templateId?: string | number | null,
): Promise<InvoiceTemplate | undefined> {
  try {
    if (templateId != null) {
      const tpl = await getInvoiceTemplate(templateId);
      if (tpl) return tpl;
    }
    const all = await getInvoiceTemplates();
    return all.find(t => t.isDefault) || all[0];
  } catch {
    return undefined;
  }
}

/**
 * Build InvoicePreviewData from a transaction, company details, and billing line items.
 * This is the data structure expected by the template-driven PDF renderer.
 */
function formatDateForPreview(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

export function buildPreviewDataForPdf(
  transaction: InvoicePdfTransaction,
  companyDetails: CompanyDetails,
  billingLineItems?: BillingInvoiceLineItem[],
): InvoicePreviewData {
  const addressParts = [
    companyDetails.addressLine1,
    companyDetails.addressLine2,
    [companyDetails.city, companyDetails.postcode].filter(Boolean).join(', '),
    companyDetails.country,
  ].filter(Boolean);

  const vatRate = 0.2;

  const lineItems: TemplateLineItem[] = billingLineItems && billingLineItems.length > 0
    ? billingLineItems.map((item, idx) => ({
        id: `line-${idx}`,
        date: item.wipDate ? formatDateForPreview(item.wipDate) : '',
        description: item.description || 'Professional services',
        costCentreCode: '',
        costCentreName: '',
        person: item.wipResourceName || '',
        role: item.wipRoleName || '',
        phase: '',
        task: '',
        qty: item.quantity,
        net: item.netAmount,
        vat: item.taxAmount,
        vatCode: item.taxAmount > 0 ? '20%' : '',
        amount: item.grossAmount > 0 ? item.grossAmount : item.netAmount,
      }))
    : [{
        id: 'line-0',
        date: '',
        description: transaction.description || 'Professional services',
        costCentreCode: '',
        costCentreName: '',
        person: '',
        role: '',
        phase: '',
        task: '',
        qty: 1,
        net: transaction.amount,
        vat: 0,
        vatCode: '',
        amount: transaction.amount,
      }];

  const subtotal = billingLineItems && billingLineItems.length > 0
    ? billingLineItems.reduce((sum, item) => sum + item.netAmount, 0)
    : transaction.amount;
  const tax = billingLineItems && billingLineItems.length > 0
    ? billingLineItems.reduce((sum, item) => sum + item.taxAmount, 0)
    : subtotal * vatRate;

  return {
    company: {
      name: companyDetails.name,
      address: addressParts.join('\n'),
      phone: companyDetails.phone || '',
      email: companyDetails.email || '',
      logo: companyDetails.logoUrl,
      vatNumber: companyDetails.vatNumber,
      registrationNumber: companyDetails.companyNumber,
      bankDetails: companyDetails.bankDetails,
    },
    invoice: {
      number: transaction.invoiceNumber,
      date: formatDateForPreview(transaction.date),
      dueDate: transaction.dueDate ? formatDateForPreview(transaction.dueDate) : '',
    },
    client: {
      name: transaction.accountName || 'Client',
      address: '',
      contact: '',
    },
    project: {
      name: transaction.projectName || '',
      description: transaction.projectDescription || '',
      projectManager: transaction.projectManager || '',
    },
    billingPeriod: { start: '', end: '' },
    lineItems,
    totals: {
      subtotal,
      tax,
      discount: 0,
      total: subtotal + tax,
    },
  };
}

/**
 * Generate, save, and return the PDF result in one step.
 * If invoiceId is provided, the PDF will be persisted to backend storage.
 *
 * When template/previewData are not provided, this function fetches the
 * default template from the API and builds preview data automatically.
 *
 * @param billingLineItems — actual line items from the backend.
 */
export async function generateAndStoreInvoicePdf(
  transaction: InvoicePdfTransaction,
  invoiceId?: number | string,
  billingLineItems?: BillingInvoiceLineItem[],
  companyDetails?: CompanyDetails,
  template?: InvoiceTemplate,
  previewData?: InvoicePreviewData,
  templateId?: string | number | null,
): Promise<InvoicePdfResult> {
  const resolvedCompany = companyDetails ?? EMPTY_COMPANY_DETAILS;

  let resolvedTemplate = template;
  let resolvedPreviewData = previewData;

  if (!resolvedTemplate) {
    resolvedTemplate = await fetchDefaultInvoiceTemplate(templateId);
  }
  if (resolvedTemplate && !resolvedPreviewData) {
    resolvedPreviewData = buildPreviewDataForPdf(transaction, resolvedCompany, billingLineItems);
  }

  const result = await generateInvoicePdf(transaction, resolvedCompany, billingLineItems, resolvedTemplate, resolvedPreviewData);
  await saveInvoicePdf(result, invoiceId);
  return result;
}

/**
 * Open a PDF blob in a new browser tab for viewing / printing.
 */
export function openPdfInNewTab(pdfBlob: Blob): void {
  const url = URL.createObjectURL(pdfBlob);
  window.open(url, '_blank');
  // Revoke after a delay to allow the tab to load
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/**
 * Trigger a download of the PDF blob.
 */
export function downloadPdf(pdfBlob: Blob, filename: string): void {
  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
